import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ImageComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  /**
   * Optional height/aspect classes for the inner image container.
   * Defaults to `aspect-video` to preserve existing layouts.
   */
  heightClassName?: string;
}

export const ImageComparisonSlider = ({ beforeImage, afterImage, heightClassName = "aspect-video" }: ImageComparisonSliderProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [afterLoaded, setAfterLoaded] = useState(false);
  const [beforeLoaded, setBeforeLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setSliderPosition(Math.min(Math.max(percentage, 0), 100));
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) handleMove(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging) handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  // Preload after image when it changes
  useEffect(() => {
    setAfterLoaded(false);
    const img = new Image();
    img.onload = () => setAfterLoaded(true);
    img.src = afterImage;
  }, [afterImage]);

  // Preload before image when it changes
  useEffect(() => {
    setBeforeLoaded(false);
    const img = new Image();
    img.onload = () => setBeforeLoaded(true);
    img.src = beforeImage;
  }, [beforeImage]);

  const isLoading = !afterLoaded || !beforeLoaded;

  return (
    <Card className="relative overflow-hidden rounded-lg shadow-card select-none" ref={containerRef}>
      <div className={`relative w-full ${heightClassName}`}>
        {/* Loading skeleton */}
        {isLoading && (
          <div className="absolute inset-0 z-10">
            <Skeleton className="w-full h-full" />
          </div>
        )}

        {/* After Image (Background) */}
        <div className={`absolute inset-0 transition-opacity duration-300 ${afterLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <img src={afterImage} alt="After" className="w-full h-full object-cover" />
          <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-0 rounded-full text-xs font-medium">
            After
          </div>
        </div>

        {/* Before Image (Clipped) */}
        <div
          className={`absolute inset-0 overflow-hidden transition-opacity duration-300 ${beforeLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img src={beforeImage} alt="Before" className="w-full h-full object-cover" />
          <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-3 py-0 rounded-full text-xs font-medium">
            Before
          </div>
        </div>

        {/* Slider Line - only show when loaded */}
        {!isLoading && (
          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-elegant cursor-ew-resize"
            style={{ left: `${sliderPosition}%` }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-elegant flex items-center justify-center">
              <div className="flex gap-1">
                <div className="w-0.5 h-6 bg-primary"></div>
                <div className="w-0.5 h-6 bg-primary"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
